import React, { useState, useCallback } from 'react';
import { Community } from 'store/community/type';
import styled from 'styled-components';

import {
  calculateTimeStamp,
  getAPIErrorMessage,
  makeAuthorizationConfig,
} from 'utils';
import { useRouter } from 'next/router';
import Modal from 'components/common/Modal';
import ModalMyProfile from 'components/community/components/popup/ModalMyProfile';
import ModalEditFeed from 'components/community/components/popup/ModalEditFeed';
import { AxiosResponse } from 'axios';
import { useCookies } from 'react-cookie';
import {
  DeleteCommunityPost,
  GetCommunityUserAPI,
  PostBlockUser,
  PostFeedLikeAPI,
  PostReportPost,
  PostUserReport,
} from 'api/community';
import { ERROR_500 } from 'constants/message';
import { default_profile } from 'constants/image';
import { resignUser } from 'constants/index';
import FeedImageSlider from './FeedImageSlider';

interface IProps {
  feed: Community;
  detail?: boolean;
  callback?: () => void;
}

const FeedCard: React.FC<IProps> = ({ feed, detail, callback }: IProps) => {
  const user = feed.user !== null ? feed.user : resignUser;
  const { profile } = user;
  const { images } = feed;

  const router = useRouter();
  const { openModal, closeModal, ModalPortal } = Modal(); // My Profile
  const {
    openModal: openEditModal,
    closeModal: closeEditModal,
    ModalPortal: EditModalPortal,
  } = Modal();
  const [cookies] = useCookies();
  const [selectUser, setSelectUser] = useState(null);

  const profile_image =
    profile.profile_picture === null
      ? default_profile
      : profile.profile_picture;

  const [showMenu, setShowMenu] = useState(false);

  const clickDetail = id => {
    router.push(`/community/post/${id}`);
  };

  const clickMyProfile = async () => {
    if (user.id === -1) {
      alert('탈퇴 회원입니다.');
      return;
    }
    const { id } = user;
    const config = makeAuthorizationConfig(cookies.access_token);

    const response: AxiosResponse = await GetCommunityUserAPI(id, config);

    if (response.status >= 400) {
      alert(ERROR_500);
      return;
    }

    setSelectUser(response.data);

    openModal();
  };

  const closeMyProfile = () => {
    closeModal();
  };

  const deleteFeed = async () => {
    if (confirm('정말로 삭제하시겠습니까?')) {
      const config = makeAuthorizationConfig(cookies.access_token);
      const result = await DeleteCommunityPost(feed.id, config);

      if (result.status === 204) {
        alert('게시물이 삭제되었습니다.');
        history.back();
      } else {
        alert('일시적인 오류로 삭제에 실패했습니다.\n다시한번 시도해주세요.');
      }
    }
  };

  const likePost = async () => {
    const config = makeAuthorizationConfig(cookies.access_token);
    const response: AxiosResponse = await PostFeedLikeAPI(feed.id, config);

    if (response.status > 400) {
      alert(getAPIErrorMessage(response));
      return;
    }

    if (detail) {
      window.location.reload();
    } else {
      callback();
    }
  };

  const reportPost = useCallback(async () => {
    if (
      !confirm(
        '해당 글을 신고하고 차단하시겠습니까?\n더 이상 해당 글이 나에게 보이지 않게 됩니다.',
      )
    )
      return;

    const config = makeAuthorizationConfig(cookies.access_token);
    const response: AxiosResponse = await PostReportPost(
      {
        url: `/community/post/${feed.id}`,
        post: feed.id,
      },
      config,
    );

    if (response.status >= 400) {
      alert(getAPIErrorMessage(response));
      return;
    }

    alert('게시글 신고 및 차단이 완료되었습니다!');
    window.location.reload();
  }, [cookies.access_token, feed.id]);

  const blockUser = useCallback(async () => {
    if (
      !confirm(
        '정말로 해당 유저를 신고하고 차단하시겠습니까?\n한 번 차단한 유저는 다시 차단 해제할 수 없습니다.',
      )
    )
      return;

    if (user.id === -1) {
      alert('탈퇴 회원입니다.');
      return;
    }

    const config = makeAuthorizationConfig(cookies.access_token);
    const result: AxiosResponse = await PostUserReport(user.id, config);
    const response: AxiosResponse = await PostBlockUser(user.id, config);

    if (response.status >= 400 && result.status >= 400) {
      alert(getAPIErrorMessage(response));
      return;
    }

    alert('유저 신고 및 차단이 완료되었습니다!');
    window.location.reload();
  }, [cookies.access_token, user.id]);

  return (
    <Container>
      <div className="inbox">
        <div className="in-hgroup">
          <span
            className="profile-img"
            style={{ backgroundImage: `url(${profile_image})` }}
          />
          <div
            className="pro-info"
            onClick={clickMyProfile}
            style={{ cursor: 'pointer' }}
          >
            <em>{profile.display_name}</em>
            <span>{calculateTimeStamp(feed.created_at)}</span>
          </div>
        </div>
        <div className="photo-w">
          <FeedImageSlider
            images={images}
            onImageClick={() => clickDetail(feed.id)}
          />
        </div>
        <div className="info">
          <strong>{feed.branch ? feed.branch.name : ''}</strong>
          <div
            dangerouslySetInnerHTML={{ __html: feed.content }}
            onClick={!detail ? () => clickDetail(feed.id) : null}
            style={
              !detail
                ? { cursor: 'pointer', whiteSpace: 'pre-wrap' }
                : { whiteSpace: 'pre-wrap' }
            }
          />
          <div
            className="side-info"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <LikeButton onClick={likePost}>
              <img
                src={`/images/heart-${
                  feed.is_liked ? 'filled' : 'outline'
                }.svg`}
                style={{ width: 16, height: 16 }}
                alt="좋아요"
              />
              <span style={{ marginLeft: 4 }}>{feed.like_count}</span>
            </LikeButton>
            <a onClick={() => clickDetail(feed.id)}>
              <span>코멘트</span>
              {feed.comment_count}
            </a>
          </div>
        </div>
        {feed.can_delete_edit ? (
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: 10,
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <button
              style={{ color: 'gray', marginRight: 8 }}
              onClick={openEditModal}
            >
              수정
            </button>
            <button style={{ color: 'gray' }} onClick={deleteFeed}>
              삭제
            </button>
          </div>
        ) : (
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: 16,
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              style={{ fill: 'rgba(0, 0, 0, 1)', cursor: 'pointer' }}
              onClick={() => setShowMenu(p => !p)}
            >
              <path d="M12 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 12c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
            </svg>
            <div
              style={{
                display: showMenu ? 'flex' : 'none',
                position: 'absolute',
                top: 36,
                right: 0,
                width: 150,
                height: 100,
                backgroundColor: 'white',
                border: '1px solid #111111',
                zIndex: 10,
                flexDirection: 'column',
              }}
            >
              <MenuItem onClick={reportPost}>유저 신고/차단하기</MenuItem>
              <MenuItem onClick={blockUser}>게시글 신고/차단하기</MenuItem>
            </div>
          </div>
        )}
      </div>
      <ModalPortal>
        <ModalMyProfile
          closeModal={closeMyProfile}
          profile={selectUser}
          user={user}
        />
      </ModalPortal>
      <EditModalPortal>
        <ModalEditFeed closeModal={closeEditModal} feed={feed} />
      </EditModalPortal>
    </Container>
  );
};

const Container = styled.div`
  .mySwiper {
    --swiper-theme-color: white !important;

    .swiper-button-prev {
      top: 50% !important;
    }
    .swiper-button-next {
      top: 50% !important;
    }

    img {
      width: 100%;
    }
  }
`;

const MenuItem = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
`;

const LikeButton = styled.div`
  display: inline-block;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
`;

export default FeedCard;
